import * as i0 from '@angular/core';
import { forwardRef, Component, Input } from '@angular/core';
import { NG_VALUE_ACCESSOR, FormsModule, ReactiveFormsModule } from '@angular/forms';
import * as i1 from '@angular/common';
import { CommonModule } from '@angular/common';
import { getFormControlFrom } from '@elotech/angular-components/utils';
import { v4 } from 'uuid';
import * as i2 from '@elotech/angular-components/pipes';
import { PipesModule } from '@elotech/angular-components/pipes';

class InputFileUploadComponent {
    writeValue(value) {
        this.files = value;
    }
    registerOnChange(fn) {
        this.onChange = fn;
    }
    registerOnTouched(fn) {
        this.onTouched = fn;
    }
    setDisabledState(isDisabled) {
        this.disabled = isDisabled;
    }
    constructor(injector) {
        this.injector = injector;
        this.id = v4();
        this.disabled = false;
        this.placeholder = '';
        this.files = [];
        this.touched = false;
        this.onChange = (value) => { };
        this.onTouched = () => { };
    }
    ngOnInit() {
        this.formControl = getFormControlFrom(this.injector);
    }
    download(fileObject) {
        fileObject.loading = true;
        this.fileDownloader(fileObject).then(() => {
            fileObject.loading = false;
        });
    }
    remove(fileObject) {
        this.files.splice(this.files.indexOf(fileObject), 1);
        this.onChange(this.files);
        this.onTouched();
    }
    handleInputChange(event) {
        Array.from(event.target.files)
            .forEach((file) => {
            const fileObject = {
                name: file.name,
                size: file.size,
                type: file.type,
                loading: true
            };
            if (this.multiple) {
                this.files.push(fileObject);
            }
            else {
                this.files = [fileObject];
            }
            if (!this.validateFileType(file)) {
                fileObject.error = 'O tipo do arquivo não é suportado';
            }
            if (!this.fileProcessor) {
                fileObject.error = 'O processador de arquivos não está configurado, não será possivel enviar arquivos.';
            }
            this.onChange(this.files);
            this.onTouched();
            if (this.fileProcessor && !fileObject.error) {
                this.fileProcessor(fileObject, file).then((key) => {
                    fileObject.loading = false;
                    fileObject.key = key;
                }).catch(() => {
                    fileObject.loading = false;
                    fileObject.error = 'Ocorreu um erro ao enviar o arquivo, remova e tente novamente.';
                }).finally(() => {
                    this.onChange(this.files);
                    this.onTouched();
                });
            }
            else {
                fileObject.loading = false;
                this.onChange(this.files);
                this.onTouched();
            }
        });
    }
    validateFileType(file) {
        if (!this.accept)
            return true;
        const acceptedTypes = this.accept.split(',').map(type => type.trim());
        const mimeType = file.type;
        const fileExtension = `.${file.name.split('.').pop()?.toLowerCase()}`;
        return acceptedTypes.some(type => type === mimeType ||
            (type.startsWith('.') && type === fileExtension) ||
            (type.endsWith('/*') && mimeType.startsWith(type.split('/')[0] + '/')));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: InputFileUploadComponent, deps: [{ token: i0.Injector }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.12", type: InputFileUploadComponent, isStandalone: true, selector: "elo-input-file-upload", inputs: { required: "required", disabled: "disabled", accept: "accept", multiple: "multiple", placeholder: "placeholder", fileProcessor: "fileProcessor", fileDownloader: "fileDownloader" }, providers: [
            {
                provide: NG_VALUE_ACCESSOR,
                multi: true,
                useExisting: forwardRef(() => InputFileUploadComponent),
            }
        ], ngImport: i0, template: `

    <div class="file-uploader">
      <input #inputFile type="file" class="file-uploader-input" 
          [id]="id"
          type="file"
          (change)="handleInputChange($event)"
          [attr.accept]="accept ? accept : null"
          [multiple]="multiple ? multiple: null"
          [required]="required"
          [disabled]="disabled" />
      <div class="input" [class.required]="required">
          <button (click)="inputFile.click()"> Escolher arquivo </button>
          <span *ngIf="!files?.length" (click)="inputFile.click()" class="empty"> Nenhum arquivo selecionado </span>
          <div *ngIf="files?.length" class="file-uploader-content">
            <ng-container *ngFor="let file of files" >
              <span class="file-container" [class.file-loading]="file.loading" [title]="file.error ? file.error : file.name" [class.error]="file.error" [class.disabled]="!fileDownloader || file.loading || file.error" #fileName>

                <ng-template #loading>
                  <i class="fas fa-circle-notch fa-spin" style="margin-right: 5px;"></i>
                </ng-template>

                <i *ngIf="!file.loading; else loading" class="fas fa-file" style="margin-right: 5px;"></i>

                <ng-template #withoutDot>
                  <span class="file-name" *ngIf="!file.name.includes('.')" (click)="fileName.classList.contains('disabled') ? null: download(file)">
                    {{file.name}}
                  </span>
                </ng-template>

                <ng-container *ngIf="file.name.includes('.'); else withoutDot">
                  <span class="file-name" style=" text-wrap: nowrap;overflow: hidden;text-overflow: ellipsis;" (click)="fileName.classList.contains('disabled') ? null: download(file)">
                    <span>
                      {{file.name.split('.').slice(0,-1)}}.
                    </span>
                  </span>
                  <span class="file-name" (click)="fileName.classList.contains('disabled') ? null: download(file)">{{file.name.split('.').pop()}}</span>
                </ng-container>

                <span style="margin-left: 5px; font-size: 10px; color: lightslategray; text-wrap: nowrap;">({{file.size | niceBytes}})</span>

                <span (click)="remove(file)" class="clickable" style="margin-left: 10px; font-size: 10px; color: lightslategray; text-wrap: nowrap;"> (Remover) </span>
              </span>
            </ng-container>
          </div>

      </div>
    </div>

    
    `, isInline: true, styles: ["::ng-deep .error.ng-invalid .input:has(.file-loading){border:1px solid #d6d6d6!important}::ng-deep .error.ng-invalid .input:not(:has(.file-loading)){border:1px solid var(--negative)!important}.file-uploader input{display:none}.file-uploader .input{border:1px solid #d6d6d6;border-radius:3px;padding:0;background:#fff;display:flex;height:auto;overflow:hidden}.file-uploader .input.required{background-image:url(data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAKBAMAAABGe2PVAAAAJ1BMVEUAAAD/AAr/AAr/AAr/AAr/AAr/AAr/AAr/AAr/AAr/AAr/AAr/AArTv4iRAAAADHRSTlMAIaPW9e2OXD/HtnamBf5OAAAAM0lEQVQI12NABdIFcGaPAYylxLPCEMqMOXPmEJTJbnNMAMpkOnkExmR10EmA6RNgZEADAPBiCJ/0SV7UAAAAAElFTkSuQmCC);background-position:100%;background-repeat:no-repeat}.file-uploader .file-uploader-content{padding:4px;width:calc(100% - 126px)}.file-uploader .file-uploader-content .file-container{display:flex;align-items:center;font-size:13px;padding:2px 10px 2px 0}.file-uploader .file-uploader-content .file-container.error,.file-uploader .file-uploader-content .file-container.error i{color:#e14040;--text-color: #e14040}.file-uploader .file-uploader-content .file-container:not(.disabled):hover:not(:has(.clickable:hover)) .file-name{text-decoration:underline;cursor:pointer}.clickable:hover{text-decoration:underline;cursor:pointer}.file-uploader .file-uploader-content .file-container i{font-size:11px;text-decoration:none;color:#535353}.file-uploader .input button{border-right:1px solid #d6d6d6;padding:4px 10px;margin-right:10px;cursor:pointer;background:#e7e7e7;font-size:13px;text-wrap:nowrap}.file-uploader .input span.empty{color:#919191;font-size:13px;width:100%;padding:4px;cursor:pointer}.file-uploader .input button:hover{background:#cfcfcf}\n"], dependencies: [{ kind: "ngmodule", type: CommonModule }, { kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "ngmodule", type: FormsModule }, { kind: "ngmodule", type: ReactiveFormsModule }, { kind: "ngmodule", type: PipesModule }, { kind: "pipe", type: i2.NiceBytesPipe, name: "niceBytes" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: InputFileUploadComponent, decorators: [{
            type: Component,
            args: [{ selector: 'elo-input-file-upload', standalone: true, imports: [
                        CommonModule,
                        FormsModule,
                        ReactiveFormsModule,
                        PipesModule
                    ], template: `

    <div class="file-uploader">
      <input #inputFile type="file" class="file-uploader-input" 
          [id]="id"
          type="file"
          (change)="handleInputChange($event)"
          [attr.accept]="accept ? accept : null"
          [multiple]="multiple ? multiple: null"
          [required]="required"
          [disabled]="disabled" />
      <div class="input" [class.required]="required">
          <button (click)="inputFile.click()"> Escolher arquivo </button>
          <span *ngIf="!files?.length" (click)="inputFile.click()" class="empty"> Nenhum arquivo selecionado </span>
          <div *ngIf="files?.length" class="file-uploader-content">
            <ng-container *ngFor="let file of files" >
              <span class="file-container" [class.file-loading]="file.loading" [title]="file.error ? file.error : file.name" [class.error]="file.error" [class.disabled]="!fileDownloader || file.loading || file.error" #fileName>

                <ng-template #loading>
                  <i class="fas fa-circle-notch fa-spin" style="margin-right: 5px;"></i>
                </ng-template>

                <i *ngIf="!file.loading; else loading" class="fas fa-file" style="margin-right: 5px;"></i>

                <ng-template #withoutDot>
                  <span class="file-name" *ngIf="!file.name.includes('.')" (click)="fileName.classList.contains('disabled') ? null: download(file)">
                    {{file.name}}
                  </span>
                </ng-template>

                <ng-container *ngIf="file.name.includes('.'); else withoutDot">
                  <span class="file-name" style=" text-wrap: nowrap;overflow: hidden;text-overflow: ellipsis;" (click)="fileName.classList.contains('disabled') ? null: download(file)">
                    <span>
                      {{file.name.split('.').slice(0,-1)}}.
                    </span>
                  </span>
                  <span class="file-name" (click)="fileName.classList.contains('disabled') ? null: download(file)">{{file.name.split('.').pop()}}</span>
                </ng-container>

                <span style="margin-left: 5px; font-size: 10px; color: lightslategray; text-wrap: nowrap;">({{file.size | niceBytes}})</span>

                <span (click)="remove(file)" class="clickable" style="margin-left: 10px; font-size: 10px; color: lightslategray; text-wrap: nowrap;"> (Remover) </span>
              </span>
            </ng-container>
          </div>

      </div>
    </div>

    
    `, providers: [
                        {
                            provide: NG_VALUE_ACCESSOR,
                            multi: true,
                            useExisting: forwardRef(() => InputFileUploadComponent),
                        }
                    ], styles: ["::ng-deep .error.ng-invalid .input:has(.file-loading){border:1px solid #d6d6d6!important}::ng-deep .error.ng-invalid .input:not(:has(.file-loading)){border:1px solid var(--negative)!important}.file-uploader input{display:none}.file-uploader .input{border:1px solid #d6d6d6;border-radius:3px;padding:0;background:#fff;display:flex;height:auto;overflow:hidden}.file-uploader .input.required{background-image:url(data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAKBAMAAABGe2PVAAAAJ1BMVEUAAAD/AAr/AAr/AAr/AAr/AAr/AAr/AAr/AAr/AAr/AAr/AAr/AArTv4iRAAAADHRSTlMAIaPW9e2OXD/HtnamBf5OAAAAM0lEQVQI12NABdIFcGaPAYylxLPCEMqMOXPmEJTJbnNMAMpkOnkExmR10EmA6RNgZEADAPBiCJ/0SV7UAAAAAElFTkSuQmCC);background-position:100%;background-repeat:no-repeat}.file-uploader .file-uploader-content{padding:4px;width:calc(100% - 126px)}.file-uploader .file-uploader-content .file-container{display:flex;align-items:center;font-size:13px;padding:2px 10px 2px 0}.file-uploader .file-uploader-content .file-container.error,.file-uploader .file-uploader-content .file-container.error i{color:#e14040;--text-color: #e14040}.file-uploader .file-uploader-content .file-container:not(.disabled):hover:not(:has(.clickable:hover)) .file-name{text-decoration:underline;cursor:pointer}.clickable:hover{text-decoration:underline;cursor:pointer}.file-uploader .file-uploader-content .file-container i{font-size:11px;text-decoration:none;color:#535353}.file-uploader .input button{border-right:1px solid #d6d6d6;padding:4px 10px;margin-right:10px;cursor:pointer;background:#e7e7e7;font-size:13px;text-wrap:nowrap}.file-uploader .input span.empty{color:#919191;font-size:13px;width:100%;padding:4px;cursor:pointer}.file-uploader .input button:hover{background:#cfcfcf}\n"] }]
        }], ctorParameters: () => [{ type: i0.Injector }], propDecorators: { required: [{
                type: Input
            }], disabled: [{
                type: Input
            }], accept: [{
                type: Input
            }], multiple: [{
                type: Input
            }], placeholder: [{
                type: Input
            }], fileProcessor: [{
                type: Input
            }], fileDownloader: [{
                type: Input
            }] } });

/**
 * Generated bundle index. Do not edit.
 */

export { InputFileUploadComponent };
